import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { isStabilizedBridge, LoanSubType } from '@roc/feature-utils';
import { insertIf, PURCHASE, REFINANCE } from '@roc/feature-utils';
import { QuoteTypeStore } from '../../../stores/quoteTypeStore';
import { GlobalStore } from '@roc/feature-app-core';
import { sum } from '../../../utils/utils';

// Calculate release amount for a property
export function calculateReleaseAmount(
  property: any,
  propertyCount: number,
  loanAmount: number,
  aggregateArvOrAsis: number
) {
  if (propertyCount > 1) {
    const arvLoanToValue = aggregateArvOrAsis === 0 ? 0 : loanAmount / aggregateArvOrAsis;
    if (property.marketValueAfterCompletion != null) {
      return Number(property.marketValueAfterCompletion * arvLoanToValue * 1.2).toFixed(2);
    } else {
      return Number(((property.purchasePrice || 0) * arvLoanToValue * 1.2).toFixed(2));
    }
  } else {
    return Number(((loanAmount || 0) * 1.2).toFixed(2));
  }
}

// Calculate property allocated amount
export function calculatePropertyAllocatedAmount(
  property: any,
  propertyCount: number,
  loanAmount: number,
  aggregateArvOrAsis: number
) {
  if (propertyCount > 1) {
    const arvLoanToValue = aggregateArvOrAsis === 0 ? 0 : loanAmount / aggregateArvOrAsis;
    if (property.marketValueAfterCompletion != null) {
      return Number((property.marketValueAfterCompletion * arvLoanToValue).toFixed(2));
    } else {
      return Number(((property.purchasePrice || 0) * arvLoanToValue).toFixed(2));
    }
  } else {
    return Number((loanAmount || 0).toFixed(2));
  }
}

export const calculateThirdPartyCosts = loanAmount => {
  if (!loanAmount || loanAmount < 0) return 0;

  const THRESHOLDS = [200000, 500000];
  const RATES = [0.04, 0.03, 0.02];

  if (loanAmount <= THRESHOLDS[0]) {
    return loanAmount * RATES[0];
  } else if (loanAmount <= THRESHOLDS[1]) {
    return loanAmount * RATES[1];
  } else {
    return loanAmount * RATES[2];
  }
};

export const generateLoanDetailsTableData = (
  outputs,
  thirdPartyCosts = 0,
  loanPurpose,
  loanSubType,
  brokerType = null
) => {
  return {
    categories: [
      {
        name: 'Purchase Price or Payoff Amount',
        fees: [
          ...insertIf(PURCHASE === loanPurpose, [
            {
              name: 'Acquisition Price',
              amount: formatCurrency(outputs?.purchasePrice),
            },
          ]),
          ...insertIf(REFINANCE === loanPurpose, [
            {
              name: 'Existing Debt Payoff',
              amount: formatCurrency(outputs?.existingDebtPayoff),
            },
          ]),
          {
            name: isStabilizedBridge(loanSubType)
              ? 'Loan Amount'
              : 'Initial Loan Amount',
            amount: formatCurrency(outputs?.initialLoanAmount),
          },
        ],
        total: formatCurrency(outputs?.totalPurchasePriceOrPayoffAmount),
      },
      {
        name: 'Reserves & Escrows',
        fees: [
          {
            name: `Interest Reserve (${outputs?.interestReserveFinanced ? 'Financed' : 'Minimum prepaid'
              })`,
            amount: formatCurrency(outputs?.interestReserve),
          },
        ],
        total: formatCurrency(outputs?.interestReserve),
      },
      {
        name: 'Lender Fees',
        fees: (outputs?.lenderFees ?? []).map(fee => ({
          name: fee.feeName,
          amount: formatCurrency(fee.feeAmount),
        })),
        total: formatCurrency(outputs?.totalLenderFees),
      },
      {
        name: 'Estimated Third Party Fees',
        fees: (outputs?.thirdPartyFees ?? [])
          .filter(fee => {
            if (fee.feeName === 'Broker Origination Fees') {
              return brokerType === 'ON_THE_HUD';
              ;
            }
            return true;
          })
          .map(fee => ({
            name: fee.feeName,
            amount: formatCurrency(fee.feeAmount),
          })),
        total: formatCurrency(outputs?.totalThirdPartyFees),
      },
    ],
    total: formatCurrency(outputs?.estimatedBorrowerCashToClose),
  };
};

const adjustOriginationFeesForBrokerType = (outputs, brokerType, brokerFee) => {
  if (brokerType !== 'OFF_THE_HUD' || !brokerFee) {
    return outputs;
  }

  const originationFee = outputs?.lenderFees?.find(
    fee => fee.feeName === 'Origination Fees'
  );

  if (!originationFee) {
    return outputs;
  }

  const combinedOriginationFees = originationFee.feeAmount + brokerFee.feeAmount;
  const otherLenderFees = outputs.lenderFees.filter(
    fee => fee.feeName !== 'Origination Fees'
  );
  const otherLenderFeesTotal = otherLenderFees.reduce(
    (sum, fee) => sum + fee.feeAmount,
    0
  );

  return {
    ...outputs,
    lenderFees: [
      { feeName: 'Origination Fees', feeAmount: combinedOriginationFees },
      ...otherLenderFees,
    ],
    totalLenderFees: combinedOriginationFees + otherLenderFeesTotal,
  };
};

export const mapDownloadableQuoteData = (
  store: QuoteTypeStore,
  globalStore: GlobalStore
) => {
  const properties: any[] = store.propertiesStore.getProperties();
  const propertyCount = properties.length;
  const loanAmount = store.loanEconomicsStore.outputs?.totalLoanAmount || 0;
  // Sum afterRepairValue or asIsValue for all properties
  const aggregateArvOrAsis = properties.reduce((sum, p) => {
    return sum + Number(p.marketValueAfterCompletion || p.purchasePrice || 0);
  }, 0);

  // Add releaseAmount to each property
  const propertiesWithReleaseAmount = properties.map(property => ({
    ...property,
    releaseAmount: calculateReleaseAmount(property, propertyCount, loanAmount, aggregateArvOrAsis),
    propertyLoanAllocatedAmount: calculatePropertyAllocatedAmount(property, propertyCount, loanAmount, aggregateArvOrAsis),
  }));

  const calculateTotalRenovationBudget = () => {
    return properties.reduce((sum, property) => {
      const renovationBudget = property.renovationBudget || 0;
      return sum + renovationBudget;
    }, 0);
  }
  const calculateConstHoldbackRenoPercent = () => {
    const constructionHoldback = store.loanEconomicsStore.outputs?.constructionHoldback || 0;
    const renovationBudget = calculateTotalRenovationBudget();
    if (renovationBudget === 0) return 0;
    return (constructionHoldback / renovationBudget ) * 100;
  }

  const borrowers = store.borrowersStore.getBorrowers().map(borrower => ({
    ...borrower,
    experience: borrower.numberOfVerifiedInvestmentProperties,
  }));

  const broker = store.brokerStore.getBroker();
  const brokerType = broker?.brokerType ?? null;
  const brokerFee = store.loanEconomicsStore.outputs?.thirdPartyFees?.find(
    fee => fee.feeName === 'Broker Origination Fees'
  );
  const fundedRetailLoanBorrower = store.borrowersStore
    .getBorrowers()
    .some(borrower => (borrower.fundedRetailLoans ?? 0) > 0);

  const modifiedOutputs = adjustOriginationFeesForBrokerType(
    store.loanEconomicsStore.outputs,
    brokerType,
    brokerFee
  );
  return {
    loanSubtype: store.loanSubtype,
    loanPurpose: store.propertiesStore.getProperties()?.[0]?.loanPurpose,
    properties: propertiesWithReleaseAmount, // include releaseAmount and allocatedAmount
    borrowers,
    entity: store.entityStore.getEntity(),
    leverageException: store.loanEconomicsStore.enableManualQuote,

    ltv: store.loanEconomicsStore.calculatedFields.calculatedLTV,
    arltv: store.loanEconomicsStore.calculatedFields.afterRepairLTV,
    ltfc: store.loanEconomicsStore.calculatedFields.ltfc ?? store.loanEconomicsStore.calculatedFields.calculatedLTFC,
    ltcCatchupDraw: store.loanEconomicsStore.calculatedFields?.ltcCatchupDrawAmount,
    brokerName: broker?.hasBroker ? broker?.companyName?.trim() : null,
    brokerFee: broker?.hasBroker
      ? `${formatCurrency(brokerFee?.feeAmount)} (${formatPercentage(
        brokerFee?.feePoints
      )})`
      : null,

    interestPaymentType: store.loanEconomicsStore.interestType,
    showFreeAppraisalNotification:
      !fundedRetailLoanBorrower &&
      !store.brokerStore.brokerFormStore.getValue('hasBroker') &&
      globalStore?.lenderInfo?.isInternal,

    showFreeAppraisalNotificationRealValPromo: store.internalValuationPilotSelected,
    outputs: {...modifiedOutputs, constructionHoldbackToRenovationBudgetPercentage: calculateConstHoldbackRenoPercent()},
    economics: store.loanEconomicsStore.getLoanEconomics(),
    brokerType,
  };
};

const parseCurrencyValue = (val: any): number => {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') return parseFloat(val.replace(/[$,\s]/g, '')) || 0;
  return 0;
};

const parsePercentageValue = (val: any): number => {
  if (typeof val === 'number') return val;
  if (typeof val === 'string' && val.includes('%')) return parseFloat(val) / 100;
  return parseFloat(val) || 0;
};

const parseMonthsValue = (val: any): number => {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') return parseInt(val) || 0;
  return 0;
};

export const mapDownloadableTermSheetData = (
  termSheetData: any,
  globalStore: GlobalStore,
  loan: any,
) => {
  const data = termSheetData;
  const loanSubtype = data.loanProgram;
  const isFixAndFlipPro = loanSubtype === LoanSubType.FIX_AND_FLIP_PRO;
  // Load Borrowers
  const borrowers = data.borrowers.map((borrower, index) => {
    const [firstName = '', lastName = ''] = (borrower.fullName || '')
      .trim()
      .split(' ');

    return {
      firstName,
      lastName,
      ficoScore: borrower.ficoScore || '',
      percentageOfOwnership: borrower.percentageOfOwnership || 0,
      experience: borrower.experienceScore,
    };
  });

  const mapLoanPurpose = {
    Refi: REFINANCE,
    'Purchase and Rehab': PURCHASE,
  };

  // Load Property
  const properties = data.properties.map((property, index) => {
    return {
      ...property,
      purchasePrice: parseCurrencyValue(property.purchasePrice),
      renovationBudget: parseCurrencyValue(property.renovationBudget),
      payoffAmount: parseCurrencyValue(property.payoffAmount),
      currentMarketValue: parseCurrencyValue(property.asIsValue),
      constructionRenoSpendToDate: parseCurrencyValue(property.completedRenoToDate),
      marketValueAfterCompletion: parseCurrencyValue(property.afterRepairValue),
      monthlyMarketRent: parseCurrencyValue(property.monthlyMarketRent),
      annualTaxes: parseCurrencyValue(property.annualTaxes),
      annualInsurance: parseCurrencyValue(property.annualInsurance),
      loanPurpose: mapLoanPurpose[data.loanPurpose] || data.loanPurpose,
    };
  });

  // Load Economics
  const totalPurchasePrice = properties.reduce((sum, property) => {
    return sum + (property?.purchasePrice || 0);
  }, 0);

  //Entity
  const entityName = data.borrowerEntity ?? '';
  // outputs
  //this.loanEconomicsStore.interestReserveFinanced = data.interestReserveFinanced;

  let interestType;
  if (loanSubtype === LoanSubType.STABILIZED_BRIDGE) {
    interestType = 'Interest Only';
  } else {
    interestType =
      data.fullBoatInterest === 'No' ? 'As disbursed' : 'Full Boat';
  }
  const lenderOriginationFees = parseCurrencyValue(data.lenderOriginationFees);
  const underwritingFee = parseCurrencyValue(data.underwritingFee);
  const processingFee = parseCurrencyValue(data.processingFee);
  const buyDownFee = parseCurrencyValue(data.buyDownFee);
  const adminFee = parseCurrencyValue(data.adminFee);
  const commitmentFee = parseCurrencyValue(data.commitmentFee);
  const brokerOriginationFees = parseCurrencyValue(data.brokerOriginationFees);
  const servicingSetupFee = parseCurrencyValue(data.servicingSetupFee);
  const legalReviewFee = parseCurrencyValue(data.legalReviewFee);
  const corelogixFee = parseCurrencyValue(data.corelogixFee);
  const interestReserve = parseCurrencyValue(data.interestReserve);
  const totalPurchasePriceOrPayoffAmount = parseCurrencyValue(data.totalPurchasePriceOrPayoffAmount);

  const outputs = {
    interestReserveFinanced: data.interestReserveFinanced,
    ltcCatchupDrawAmount: parseCurrencyValue(data.ltcCatchupDrawAmount),
    totalPurchasePriceOrPayoffAmount,
    totalLoanAmount: parseCurrencyValue(data.totalLoanAmount),
    initialLoanAmount: parseCurrencyValue(data.initialLoanAmount),
    constructionHoldback: parseCurrencyValue(data.constructionHoldback),
    interestReserve,
    interestReserveMonths: data.interestReserveMonths,
    existingDebtPayoff: parseCurrencyValue(data.existingDebtPayoff),
    purchasePrice: totalPurchasePrice,
    constructionHoldbackToRenovationBudgetPercentage: parsePercentageValue(data.constructionHoldbackToRenovationBudgetPercentage),
    lenderFees: [
      { feeName: 'Origination Fees', feeAmount: lenderOriginationFees },
      { feeName: 'Underwriting Fee', feeAmount: underwritingFee },
      { feeName: 'Processing Fee', feeAmount: processingFee },
      { feeName: 'Buy Down Fee', feeAmount: buyDownFee },
      { feeName: 'Admin Fee', feeAmount: adminFee },
      { feeName: 'Commitment Fee', feeAmount: commitmentFee },
    ].filter(fee => fee.feeAmount !== 0),
    totalLenderFees: sum([lenderOriginationFees, underwritingFee, processingFee, buyDownFee, adminFee, commitmentFee]),
    thirdPartyFees: [
      { feeName: 'Broker Origination Fees', feeAmount: brokerOriginationFees },
      { feeName: 'Servicing Setup Fee', feeAmount: servicingSetupFee },
      { feeName: 'Legal Review Fee', feeAmount: legalReviewFee },
      { feeName: 'Corelogix Fee', feeAmount: corelogixFee },
      { feeName: 'Title, Insurance and Other Estimated Costs', feeAmount: parseCurrencyValue(data.thirdPartyCosts) },
    ].filter(fee => !(isFixAndFlipPro && ['Legal Review Fee', 'Corelogix Fee'].includes(fee.feeName))),
    totalThirdPartyFees: legalReviewFee + servicingSetupFee + brokerOriginationFees + corelogixFee,
    estimatedBorrowerCashToClose:
      legalReviewFee + interestReserve + servicingSetupFee + brokerOriginationFees +
      lenderOriginationFees + underwritingFee + totalPurchasePriceOrPayoffAmount +
      corelogixFee + adminFee + buyDownFee + commitmentFee + processingFee,
  };
  // Economics
  const economics = {
    interestRate: parsePercentageValue(data.interestRate),
    loanTermMonths: parseMonthsValue(data.duration),
    loanToCost: parsePercentageValue(data.initialLTC),
    manualQuoteLoantoCost: parsePercentageValue(data.initialLTC),
    enableManualQuote: true,
    interestReserveFinanced: data.interestReserveFinanced,
  };

  const fundedRetailLoanBorrower = borrowers.some(
    borrower => (borrower.fundedRetailLoans ?? 0) > 0
  );
  const hasBroker = !!(data.brokerName || '').trim();
  return {
    loanSubtype: data.loanProgram,
    loanPurpose: properties?.[0]?.loanPurpose,
    properties: properties,
    borrowers: borrowers,
    entity: {
      entityName,
    },
    leverageException: false,

    ltv: parsePercentageValue(data.ltv),
    arltv: parsePercentageValue(data.arltv),
    ltfc: parsePercentageValue(data.ltfc),

    brokerName: data.brokerName,
    brokerFee: data.brokerOriginationFees,
    brokerType: data.brokerType,

    interestPaymentType: interestType,
    showFirstTimeBorrowerFreeAppraisalNotification: loan?.firstTimeBorrowerFreeAppraisal,
    showFreeAppraisalPromotionNotification: loan?.freeAppraisalPromotion && !loan?.internalValuationPilotSelected,
    showFreeAppraisalNotificationRealValPromo: loan?.internalValuationPilotSelected || data.internalValuationFeeSelected,

    outputs: outputs,
    economics: economics,
  };
};
